import {useCallback, useEffect, useRef, useState} from "react";
import {useCookies} from "react-cookie";

const COOKIE_NAME = "uptime_sp_auto_refresh";
const COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
const TICK_INTERVAL_MS = 1000;

export const AUTO_REFRESH_INTERVALS = [0, 2, 5, 10, 15, 30, 60] as const;
export type AutoRefreshInterval = (typeof AUTO_REFRESH_INTERVALS)[number];

interface UseAutoRefreshOptions {
  onRefresh: () => void;
  pageKey: string | undefined;
}

export interface AutoRefreshState {
  interval: AutoRefreshInterval;
  secondsRemaining: number;
  isActive: boolean;
  progressPercent: number;
  setAutoRefreshInterval: (interval: AutoRefreshInterval) => void;
  refreshNow: () => void;
}

export const useAutoRefresh = ({onRefresh, pageKey}: UseAutoRefreshOptions): AutoRefreshState => {
  const [cookies, setCookie] = useCookies([COOKIE_NAME]);
  const [interval, setIntervalState] = useState<AutoRefreshInterval>(0);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  // Initialize from cookie when pageKey becomes available, reset when it changes
  useEffect(() => {
    if (!pageKey) return;
    const settings = cookies[COOKIE_NAME];
    const saved = settings?.[pageKey];
    const resolved = saved != null && AUTO_REFRESH_INTERVALS.includes(saved) ? (saved as AutoRefreshInterval) : 0;
    setIntervalState(resolved);
    setSecondsRemaining(resolved * 60);
  }, [pageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Timer tick
  useEffect(() => {
    if (interval === 0) return;

    const timerId = window.setInterval(() => {
      setSecondsRemaining((prev) => {
        const next = prev - 1;
        if (next <= 0 && document.visibilityState === "visible") {
          onRefreshRef.current();
          return interval * 60;
        }
        return Math.max(0, next);
      });
    }, TICK_INTERVAL_MS);

    return () => window.clearInterval(timerId);
  }, [interval]);

  const setAutoRefreshInterval = useCallback(
    (newInterval: AutoRefreshInterval) => {
      setIntervalState(newInterval);
      setSecondsRemaining(newInterval * 60);
      if (pageKey) {
        const settings = cookies[COOKIE_NAME] ?? {};
        setCookie(COOKIE_NAME, {...settings, [pageKey]: newInterval}, {path: "/", maxAge: COOKIE_MAX_AGE_SECONDS});
      }
    },
    [pageKey, cookies, setCookie]
  );

  const refreshNow = useCallback(() => {
    onRefreshRef.current();
    setSecondsRemaining(interval * 60);
  }, [interval]);

  const isActive = interval > 0;
  const totalSeconds = interval * 60;
  const progressPercent = isActive && totalSeconds > 0 ? ((totalSeconds - secondsRemaining) / totalSeconds) * 100 : 0;

  return {
    interval,
    secondsRemaining,
    isActive,
    progressPercent,
    setAutoRefreshInterval,
    refreshNow,
  };
};
